// Pure helpers for reading and writing a Work node's per-set `setScheme`.
//
// Backs the per-set editor in ExerciseDetailDialog (issue #69) and the load
// editors in utils/sessionLoads.js.
//
// Lives in utils/ rather than beside the dialog so the schema contract it has
// to satisfy — "only varying dimensions are arrayed", "static mirrors set 1",
// "one entry per set" (docs/schema.md) — can be unit-tested without importing
// the dialog, which pulls in llmApi and its ESM-only Gemini dependency.

const toNumber = (input) => {
  if (input === "" || input === null || input === undefined) return null;
  const parsed = Number(input);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Seed one row of the per-set editor: `setCount` cells, taking each set's own
 * figure from the scheme array where it has one and the static field
 * everywhere else. Returns strings, because the cells back controlled inputs.
 *
 * A node that runs once has no per-set row to edit, so anything below 2 sets
 * yields no cells — callers can key the whole control off the row being empty.
 */
export const seedSetCells = (schemeArray, staticValue, setCount) => {
  const count =
    Number.isFinite(setCount) && setCount > 1 ? Math.floor(setCount) : 0;
  const fallback = toNumber(staticValue);
  return Array.from({ length: count }, (_, i) => {
    const entry = Array.isArray(schemeArray) ? toNumber(schemeArray[i]) : null;
    const resolved = entry !== null ? entry : fallback;
    return resolved === null ? "" : String(resolved);
  });
};

/**
 * Collapse one edited row back into the pair the schema wants: the static
 * field — always set 1 — and the array, present ONLY when the sets actually
 * differ, since a constant parameter stays on the static field.
 *
 * A blank or unparseable cell falls back to `staticValue`, so a half-filled row
 * can never write a hole into the plan. With no fallback to fall back to, the
 * row is discarded whole rather than written partially.
 *
 * @returns {{ value: number|null, array: number[]|null }}
 */
export const resolveSetCells = (cells, staticValue) => {
  const fallback = toNumber(staticValue);
  const list = Array.isArray(cells) ? cells : [];
  const numbers = list.map((cell) => {
    const parsed = toNumber(cell);
    return parsed !== null ? parsed : fallback;
  });
  if (numbers.length === 0 || numbers.some((n) => n === null)) {
    return { value: fallback, array: null };
  }
  const varies = numbers.some((n) => n !== numbers[0]);
  return { value: numbers[0], array: varies ? numbers : null };
};
